'use client';

import { useEffect } from 'react';
import type { EarlyError } from './sentry';

/** Quanto aspettare, al massimo, un momento di quiete prima di avviare Sentry. */
const BOOT_TIMEOUT_MS = 4000;

/**
 * Sentry parte quando la pagina è già utilizzabile, non insieme
 * all'idratazione: il suo bundle (una sessantina di KB compressi) si
 * contendeva il thread principale con React proprio nei primi secondi, quelli
 * in cui si decide la reattività al primo tocco. Il modulo si scarica con un
 * `import()` a parte, in un momento di quiete del browser.
 *
 * Gli errori di quei primi secondi non si perdono: due listener li tengono da
 * parte e `initializeSentry` li consegna appena è pronto.
 */
export function SentryProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    if (!process.env.NEXT_PUBLIC_SENTRY_DSN) return;

    const early: EarlyError[] = [];
    const onError = (event: ErrorEvent) =>
      early.push({ kind: 'error', error: event.error, message: event.message });
    const onRejection = (event: PromiseRejectionEvent) =>
      early.push({ kind: 'rejection', reason: event.reason });
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    const stopListening = () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };

    let booted = false;
    const boot = () => {
      if (booted) return;
      booted = true;
      import('./sentry').then(({ initializeSentry }) => {
        stopListening();
        initializeSentry(early);
      });
    };

    // Quiete, o comunque entro il timeout: in una scheda in secondo piano
    // `requestIdleCallback` non scatta affatto, il timer sì
    const idle = window.requestIdleCallback?.(boot, { timeout: BOOT_TIMEOUT_MS });
    const timer = window.setTimeout(boot, BOOT_TIMEOUT_MS);

    return () => {
      booted = true;
      if (idle !== undefined) window.cancelIdleCallback(idle);
      window.clearTimeout(timer);
      stopListening();
    };
  }, []);

  return <>{children}</>;
}
